import * as T from 'three'
import type { DrawerFaceStyle, DrawerPullStyle } from '../../../../../config/blissRoomFinish'

/** Face dimensions in inches; origin at lower left, front points along +Z. */
export function createDrawerFace(width: number, height: number, style: DrawerFaceStyle = 'shaker', pull?: DrawerPullStyle) {
  const group = new T.Group()
  group.name = `drawer-face-${style}`
  function box(name: string, x: number, y: number, z: number, w: number, h: number, d: number, part = 'frame') {
    const opening = pull === 'cutout' && (name === 'shaker-top-rail' || name === 'shiplap-board-' + (Math.max(2, Math.round(height / 3.5))-1))
    const geometry = opening ? faceWithOpening(w,h,d,Math.min(6,w-1),Math.min(1.1,h-0.5),w/2,h/2) : new T.BoxGeometry(w / 12, h / 12, d / 12)
    if (opening) geometry.translate(-w/24,-h/24,-d/24)
    const mesh = new T.Mesh(geometry, new T.MeshStandardMaterial({color: part === 'recess' ? 0x292929 : 0xffffff}))
    mesh.name = name
    if (part !== 'recess') mesh.userData.finishPart = part
    mesh.position.set((x+w/2)/12,(y+h/2)/12,(z+d/2)/12)
    group.add(mesh)
    return mesh
  }
  if (style === 'shiplap') {
    const rows = Math.max(2, Math.round(height / 3.5)), gap = 0.125
    const plank = (height - (rows-1)*gap)/rows
    const backing = box('groove-backing',0,0,0,width,pull === 'cutout' ? height-plank : height,0.55)
    // Recess-only ambient occlusion: retain a readable shadow in each nickel gap.
    backing.userData.finishShade = 0.18
    backing.material.color.multiplyScalar(0.18)
    for(let i=0;i<rows;i++) box(`shiplap-board-${i}`,0,i*(plank+gap),0.55,width,plank,0.2)
  } else if (style === 'flat_pull' && (!pull || pull === 'cutout')) {
    const w = Math.min(7, width-2), h = Math.min(2, height/3), x = (width-w)/2, y = height-h-0.75, radius = h/2
    const shape = new T.Shape()
    shape.moveTo(0,0);shape.lineTo(width/12,0);shape.lineTo(width/12,height/12);shape.lineTo(0,height/12);shape.closePath()
    const hole = new T.Path()
    hole.moveTo((x+radius)/12,y/12)
    hole.lineTo((x+w-radius)/12,y/12)
    hole.absarc((x+w-radius)/12,(y+radius)/12,radius/12,-Math.PI/2,Math.PI/2,false)
    hole.lineTo((x+radius)/12,(y+h)/12)
    hole.absarc((x+radius)/12,(y+radius)/12,radius/12,Math.PI/2,Math.PI*1.5,false)
    hole.closePath();shape.holes.push(hole)
    const face = new T.Mesh(new T.ExtrudeGeometry(shape,{depth:0.75/12,bevelEnabled:false,curveSegments:20}),new T.MeshStandardMaterial({color:0xffffff}))
    face.name='flat-face-with-hand-opening';face.userData.finishPart='frame';group.add(face)
    // Soft finish-colored recess keeps the hand opening readable without a black fill.
    const interior = box('pull-opening-interior',x,y,0.51,w,h,0.01)
    interior.userData.finishShade = 0.45
    interior.material.color.multiplyScalar(0.45)
  } else if (style === 'flat_pull') {
    box('flat-face',0,0,0,width,height,0.75)
  } else {
    const rail = Math.min(1.75,height/4,width/4)
    box('shaker-inset-panel',rail,rail,0,width-2*rail,height-2*rail,0.4)
    box('shaker-left-stile',0,0,0,rail,height,0.75)
    box('shaker-right-stile',width-rail,0,0,rail,height,0.75)
    box('shaker-bottom-rail',rail,0,0,width-2*rail,rail,0.75)
    box('shaker-top-rail',rail,height-rail,0,width-2*rail,rail,0.75)
  }
  if (pull && pull !== 'cutout') group.add(createDrawerPull(width,height,pull))
  else if(!pull && style !== 'flat_pull') box('drawer-handle',(width-Math.min(6,width/2))/2,height*0.72,0.75,Math.min(6,width/2),0.4,0.4,'accent')
  return group
}

function faceWithOpening(w:number,h:number,d:number,ow:number,oh:number,cx:number,cy:number) {
  const shape=new T.Shape()
  shape.moveTo(0,0);shape.lineTo(w/12,0);shape.lineTo(w/12,h/12);shape.lineTo(0,h/12);shape.closePath()
  const hole=new T.Path(), radius=oh/2, left=cx-ow/2+radius, right=cx+ow/2-radius
  hole.moveTo(left/12,(cy-radius)/12);hole.lineTo(right/12,(cy-radius)/12)
  hole.absarc(right/12,cy/12,radius/12,-Math.PI/2,Math.PI/2,false)
  hole.lineTo(left/12,(cy+radius)/12);hole.absarc(left/12,cy/12,radius/12,Math.PI/2,Math.PI*1.5,false);hole.closePath()
  shape.holes.push(hole)
  return new T.ExtrudeGeometry(shape,{depth:d/12,bevelEnabled:false,curveSegments:16})
}

/** Hardware retains its own metal or rope finish when the room is repainted. */
function createDrawerPull(width:number,height:number,style:Exclude<DrawerPullStyle,'cutout'>) {
  const group=new T.Group();group.name='drawer-pull-'+style
  const span=Math.min(6,width/2), y=height*0.72, center=width/2
  const material=new T.MeshStandardMaterial({color:style==='gold_bar'?0xc5a35b:style==='silver_bar'?0xbec4ca:0xe5dbc3,metalness:style==='rope'?0:0.85,roughness:style==='rope'?0.95:0.3})
  const points=style==='rope'
    ? [new T.Vector3((center-span/2)/12,y/12,0.85/12),new T.Vector3((center-span/2+0.5)/12,(y-1.7)/12,1.4/12),new T.Vector3(center/12,(y-2.1)/12,1.6/12),new T.Vector3((center+span/2-0.5)/12,(y-1.7)/12,1.4/12),new T.Vector3((center+span/2)/12,y/12,0.85/12)]
    : [new T.Vector3((center-span/2)/12,y/12,1.7/12),new T.Vector3((center+span/2)/12,y/12,1.7/12)]
  const curve=new T.CatmullRomCurve3(points)
  const handle=new T.Mesh(new T.TubeGeometry(curve,32,(style==='rope'?0.22:0.18)/12,10,false),material)
  handle.name='pull-'+style;group.add(handle)
  for (const x of [center-span/2,center+span/2]) {
    const mount=new T.Mesh(new T.CylinderGeometry(0.23/12,0.23/12,0.95/12,12),material.clone())
    mount.rotation.x=Math.PI/2;mount.position.set(x/12,y/12,1.2/12);group.add(mount)
  }
  return group
}
